import { useState } from "react";
import { Loader2, Wallet } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsibleAgentSelect } from "@/components/ResponsibleAgentSelect";
import { useConvertCreditToEpargneMutation } from "@/hooks/mutations/useConvertCreditToEpargne";
import { useAuth } from "@/App";

interface ConvertCreditDialogProps {
  partyId: number;
  availableCredit: number;
}

// manager+ (route-gated server-side, same tier as souscription/dépôt) —
// standalone on-demand credit-to-épargne conversion, separate from the
// opening-time offer in SubscribeButton: this covers a party who already
// has an épargne account and picks up new credit later, or one where the
// opening-time offer was declined. The 30-day decision window is time
// allowed for the client to decide, not a ban on deciding early.
export function ConvertCreditDialog({ partyId, availableCredit }: ConvertCreditDialogProps) {
  const { user } = useAuth();
  const canPickResponsible = user ? ["manager", "admin", "super_admin"].includes(user.role) : false;
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(0);
  const [responsibleAgentId, setResponsibleAgentId] = useState<number | undefined>(undefined);
  const convertMutation = useConvertCreditToEpargneMutation(partyId);

  function reset() {
    setAmount(0);
    setResponsibleAgentId(undefined);
  }

  function handleSubmit() {
    convertMutation.mutate(
      { amount, responsibleAgentId },
      {
        onSuccess: () => {
          setOpen(false);
          reset();
        },
      },
    );
  }

  if (availableCredit <= 0) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
        else setAmount(availableCredit);
      }}
    >
      <DialogTrigger>
        <Button size="sm" variant="outline">
          <Wallet size={13} /> Convertir en épargne
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Convertir le crédit en épargne</DialogTitle>
          <DialogDescription>
            Montant à convertir — maximum {availableCredit.toLocaleString("fr-FR")} XAF.
          </DialogDescription>
        </DialogHeader>
        <div>
          <label className="block text-[11.5px] font-medium text-body mb-1.5">Montant</label>
          <Input
            type="number"
            value={amount || ""}
            onChange={(e) =>
              setAmount(Math.min(Math.max(parseFloat(e.target.value) || 0, 0), availableCredit))
            }
            max={availableCredit}
            autoFocus
          />
        </div>
        {canPickResponsible && (
          <ResponsibleAgentSelect value={responsibleAgentId} onChange={setResponsibleAgentId} />
        )}
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={convertMutation.isPending || amount <= 0 || amount > availableCredit}
          >
            {convertMutation.isPending ? <Loader2 size={13} className="animate-spin" /> : "Confirmer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
